import React, { useState } from 'react';
import { Customer } from '../types/types';
import { EditCustomerPopup } from './EditCustomerPopup';
import { AddCustomerPopup } from './AddCustomerPopup';
import { SmsMessagesDialog } from './SmsMessagesDialog';
import { apiService } from '../services/api';
import Tooltip from '@mui/material/Tooltip';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import '../styles/CustomerDataTable.css';

interface CustomerDataTableProps {
  customers: Customer[];
  onDelete: (apikey: string) => void;
  onEdit: (apikey: string, changes: Partial<Pick<Customer, 'readBatchSize' | 'readDelayMillis' | 'postURL'>>) => Promise<void>;
  onAdd: (customer: Customer) => void;
  onRefresh: () => void;
  showNotification: (message: string, type: 'success' | 'error') => void;
  readOnlyCustomers?: boolean;
}

// Helper function to format Date to 'YYYY-MM-DD HH:mm'
const formatDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// Helper function for relative time
const formatRelativeTime = (timestamp: number | undefined | null): string => {
  if (!timestamp || timestamp === 0) {
    return 'No Info available';
  }

  const now = Date.now();
  const diffInSeconds = Math.floor((now - timestamp) / 1000);

  if (diffInSeconds < 60) {
    return `${diffInSeconds} sec ago`;
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} min ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
};

export const CustomerDataTable: React.FC<CustomerDataTableProps> = ({
  customers,
  onDelete,
  onEdit,
  onAdd,
  onRefresh,
  showNotification,
  readOnlyCustomers = false,
}) => {
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [deleteCustomer, setDeleteCustomer] = useState<Customer | null>(null);
  const [showFlushDialog, setShowFlushDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [flushDate, setFlushDate] = useState('');
  const [flushTime, setFlushTime] = useState('');
  const [flushAllChecked, setFlushAllChecked] = useState(true);
  const [flushResult, setFlushResult] = useState<string | null>(null);
  const [isFlushing, setIsFlushing] = useState(false);
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const [smsCustomer, setSmsCustomer] = useState<Customer | null>(null);

  const handleEditClick = (customer: Customer) => {
    setEditingCustomer(customer);
  };

  const handleEditClose = () => {
    setEditingCustomer(null);
  };

  const handleEditSave = async (apikey: string, changes: {
    readBatchSize?: number;
    readDelayMillis?: number;
    postURL?: string;
  }) => {
    try {
      await onEdit(apikey, changes);
      handleEditClose();
    } catch (error) {
      // Error is already handled in App.tsx
      console.error('Edit error:', error);
    }
  };

  const handleDeleteClick = (customer: Customer) => {
    if (customer.pendingMessages > 0) {
      showNotification('Cannot delete customer with pending messages', 'error');
      return;
    }
    setDeleteCustomer(customer);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteCustomer) return;
    try {
      await onDelete(deleteCustomer.apikey);
      setDeleteCustomer(null);
    } catch (error) {
      showNotification('Failed to delete customer', 'error');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteCustomer(null);
  };

  const handleAddClick = () => {
    setShowAddPopup(true);
  };

  const handleAddClose = () => {
    setShowAddPopup(false);
  };

  const handleAddSave = async (newCustomer: Customer) => {
    try {
      await onAdd(newCustomer);
      handleAddClose();
    } catch (error) {
      // Error is already handled in App.tsx
      console.error('Add error:', error);
    }
  };

  const handleFlushQueue = async () => {
    if (!selectedCustomer) return;

    let dateUntil: string;

    if (flushAllChecked) {
      dateUntil = formatDateTime(new Date());
    } else {
      if (!flushDate || !flushTime) {
        setFlushResult('Please select both date and time, or check "Flush All".');
        return;
      }
      dateUntil = `${flushDate} ${flushTime}`;
    }

    setIsFlushing(true);
    setFlushResult(null);

    try {
      const response = await apiService.flushQueue(selectedCustomer.apikey, dateUntil);
      const successMessage = response.message || `Queue flushed successfully until ${dateUntil}.`;
      
      showNotification(successMessage, 'success');
      
      setShowFlushDialog(false);
      
      onRefresh();

    } catch (error: any) {
      console.error('Failed to flush queue:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to flush queue. Please try again.';
      setFlushResult(errorMessage);
    } finally {
      setIsFlushing(false);
    }
  };

  const handleSmsClick = (customer: Customer) => {
    setSmsCustomer(customer);
    setShowSmsDialog(true);
  };

  const handleSmsDialogClose = () => {
    setShowSmsDialog(false);
    setSmsCustomer(null);
  };

  return (
    <div className="customer-table-container">
      <div className="table-header">
        <div className="header-left">
          <h2>Customers</h2>
          <div className="global-settings-legend">
            <div className="legend-item">Customer Settings not defined. Global settings used</div>
          </div>
        </div>
        <div className="header-right">
          {!readOnlyCustomers && (
            <button className="button primary" onClick={handleAddClick}>
              Add Customer
            </button>
          )}
        </div>
      </div>

      <div className="table-wrapper">
        <table className="customer-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>API Key</th>
              <th>Settings</th>
              <th>Pending Messages</th>
              <th>Last Processed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer) => (
              <tr key={customer.apikey}>
                <td>{customer.name}</td>
                <td>{customer.apikey}</td>
                <td className="settings-cell">
                  <div className="settings-group">
                    <div className="setting-item">
                      <span>Read Batch Size:</span>
                      <small>{customer.readBatchSize || '*'}</small>
                    </div>
                    <div className="setting-item">
                      <span>Read Delay:</span>
                      <small>{customer.readDelayMillis || '*'}</small>
                    </div>
                    <div className="setting-item">
                      <span>Post URL:</span>
                      <small>{customer.postURL || '*'}</small>
                    </div>
                  </div>
                </td>
                <td>{customer.pendingMessages}</td>
                <td>{formatRelativeTime(customer.lastMessageTime)}</td>
                <td className="actions-cell">
                  {!readOnlyCustomers && (
                    <Tooltip title="Edit Customer Settings" arrow>
                      <button
                        className="action-button edit"
                        onClick={() => handleEditClick(customer)}
                      >
                        <EditOutlinedIcon />
                      </button>
                    </Tooltip>
                  )}
                  <Tooltip title="View Messages" arrow>
                    <button
                      className="action-button search"
                      onClick={() => handleSmsClick(customer)}
                    >
                      <SearchOutlinedIcon />
                    </button>
                  </Tooltip>
                  <Tooltip title="Flush Messages" arrow>
                    <button
                      className="action-button flush"
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setShowFlushDialog(true);
                        setFlushResult(null);
                        setFlushAllChecked(false);
                      }}
                    >
                      <CleaningServicesOutlinedIcon />
                    </button>
                  </Tooltip>
                  {!readOnlyCustomers && customer.pendingMessages === 0 && (
                    <Tooltip title="Delete Customer" arrow>
                      <button
                        className="action-button delete"
                        onClick={() => handleDeleteClick(customer)}
                      >
                        <DeleteOutlineIcon />
                      </button>
                    </Tooltip>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingCustomer && (
        <EditCustomerPopup
          customer={editingCustomer}
          onClose={handleEditClose}
          onSave={handleEditSave}
        />
      )}

      {showAddPopup && (
        <AddCustomerPopup
          onClose={handleAddClose}
          onAdd={handleAddSave}
          existingApiKeys={customers.map(c => c.apikey)}
        />
      )}

      {deleteCustomer && (
        <div className="popup-overlay">
          <div className="popup-content confirmation-dialog">
            <div className="popup-header">
              <h3>Delete Customer</h3>
              <button className="close-button" onClick={handleDeleteCancel}>×</button>
            </div>
            <div className="popup-body">
              <div className="confirmation-message">
                Are you sure you want to delete customer "{deleteCustomer.name}"?
                This action cannot be undone.
              </div>
              <div className="button-group">
                <button className="button secondary" onClick={handleDeleteCancel}>
                  Cancel
                </button>
                <button className="button primary" onClick={handleDeleteConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showFlushDialog && selectedCustomer && (
        <div className="dialog-overlay">
          <div className="dialog">
            <h3>Flush Message Queue</h3>
            <p>Select an option to flush messages for <strong>{selectedCustomer.name}</strong>.</p>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={flushAllChecked}
                  onChange={(e) => {
                    setFlushAllChecked(e.target.checked);
                    if (flushResult) setFlushResult(null);
                  }}
                />
                Flush All Messages (Overrides date/time selection)
              </label>
            </div>

            <fieldset className="date-time-fieldset" disabled={flushAllChecked}>
              <legend>Or Flush Until Specific Date/Time</legend>
              <div className="form-group">
                <label htmlFor="flush-date">Date</label>
                <input
                  id="flush-date"
                  type="date"
                  value={flushDate}
                  onChange={(e) => setFlushDate(e.target.value)}
                  disabled={flushAllChecked}
                />
              </div>
              <div className="form-group">
                <label htmlFor="flush-time">Time</label>
                <input
                  id="flush-time"
                  type="time"
                  value={flushTime}
                  onChange={(e) => setFlushTime(e.target.value)}
                  disabled={flushAllChecked}
                />
              </div>
            </fieldset>

            {flushResult && (
              <div className={`notification ${flushResult.toLowerCase().includes('fail') || flushResult.toLowerCase().includes('error') ? 'error' : 'success'}`}>
                {flushResult}
              </div>
            )}

            <div className="dialog-buttons">
              <button
                className="button secondary"
                onClick={() => setShowFlushDialog(false)}
                disabled={isFlushing}
              >
                Cancel
              </button>
              <button
                className="button primary"
                onClick={handleFlushQueue}
                disabled={
                  isFlushing ||
                  (!flushAllChecked && (!flushDate || !flushTime))
                }
              >
                {isFlushing ? 'Flushing...' : 'Flush Queue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSmsDialog && smsCustomer && (
        <SmsMessagesDialog
          apiKey={smsCustomer.apikey}
          customerName={smsCustomer.name}
          onClose={handleSmsDialogClose}
        />
      )}
    </div>
  );
}; 